import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { FeatureLineForm } from './ArcgisCivil3dFeatureLineSettingForm';
import { useSearchLineStyles } from '../../../hooks/api/use-line-styles.hook';
import { useSearchAutodeskLayers } from '../../../hooks/api/use-autodesk-layers.hook';
import { useGetSettingConfiguration } from '../../../hooks/api/use-arcgis-civil3d-setting-configurations.hook';
import { useSearchArcgisFeatureClasses } from '../../../hooks/api/use-arcgis-feature-classes.hook';
import { AutodeskLayerRelationship, WithLayer } from '../../../../../types/models/autodesk-layer.model';
import { useCreateFeatureLineSetting } from '../../../hooks/api/use-arcgis-civil3d-feature-line-settings.hook';
import { civil3dFeatureTypesByCategory, Civil3dFeatureType } from '../../../../../types/constants/civil3d-feature-type';

export const AddFeatureLineSetting = () => {
  const navigate = useNavigate();
  const { configurationSettingId } = useParams<{ configurationSettingId: string }>();

  const { loading: loadingLineStyles, response: lineStylesResponse } = useSearchLineStyles({
    limit: 99999,
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });
  const { loading: loadingAutodeskLayers, response: autodeskLayersResponse } = useSearchAutodeskLayers({
    limit: 99999,
    include: [AutodeskLayerRelationship.LAYER],
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });
  const { loading: loadingSettingConfiguration, settingConfiguration } = useGetSettingConfiguration({
    settingConfigurationId: Number(configurationSettingId),
  });
  const { loading: loadingFeatureClasses, response: featureClassesResponse } = useSearchArcgisFeatureClasses({
    limit: 99999,
  });

  const [createFeatureLineSetting, { loading: creatingFeatureLine, error: createError }] =
    useCreateFeatureLineSetting();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FeatureLineForm>({
    resolver: classValidatorResolver(FeatureLineForm),
    defaultValues: { configuration: settingConfiguration },
  });

  const handleCreateFeatureLine = async ({
    lineStyle,
    autodeskLayer,
    configuration,
    featureClass,
    ...values
  }: FeatureLineForm) => {
    await createFeatureLineSetting({
      ...values,
      lineStyleId: lineStyle?.id,
      autodeskLayerId: autodeskLayer?.id,
      configurationId: configuration?.id,
      arcgisFeatureClassId: featureClass.id,
    });
  };

  const onSubmit: SubmitHandler<FeatureLineForm> = async (values) => {
    await handleCreateFeatureLine(values);

    navigate('..');
  };

  const renderContent = () => (
    <div>
      <Controller
        control={control}
        name="configuration"
        render={({ field }) => (
          <Autocomplete
            {...register('configuration')}
            loading={loadingSettingConfiguration}
            sx={{ mt: '1rem' }}
            disabled
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(configuration) => configuration?.name ?? ''}
            options={[settingConfiguration]}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ArcGIS Civil 3D Setting Configuration"
                error={Boolean(errors.configuration)}
                helperText={errors.configuration?.message}
              />
            )}
          />
        )}
      />
      <TextField
        {...register('name')}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingFeatureLine}
        error={Boolean(errors.name)}
        helperText={errors.name?.message}
        label="Name"
        margin="dense"
        size="small"
        fullWidth
      />
      <TextField
        {...register('query')}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingFeatureLine}
        error={Boolean(errors.query)}
        helperText={errors.query?.message}
        label="Query"
        margin="dense"
        size="small"
        multiline
        rows={4}
        fullWidth
      />
      <TextField
        {...register('priority', { valueAsNumber: true })}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingFeatureLine}
        error={Boolean(errors.priority)}
        helperText={errors.priority?.message}
        label="Priority"
        margin="dense"
        size="small"
        type="number"
        fullWidth
      />
      <Controller
        control={control}
        name="lineStyle"
        render={({ field }) => (
          <Autocomplete
            {...register('lineStyle')}
            loading={loadingLineStyles}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(lineStyle) => lineStyle?.name ?? ''}
            options={lineStylesResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Style"
                error={Boolean(errors.lineStyle)}
                helperText={errors.lineStyle?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="autodeskLayer"
        render={({ field }) => (
          <Autocomplete
            loading={loadingAutodeskLayers}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOption) => field.onChange(selectedOption)}
            onBlur={field.onBlur}
            value={field.value ?? null}
            getOptionLabel={(autodeskLayer) => (autodeskLayer as WithLayer)?.layer?.name ?? ''}
            options={autodeskLayersResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Autodesk Layer"
                error={Boolean(errors.autodeskLayer)}
                helperText={errors.autodeskLayer?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="featureClass"
        render={({ field }) => (
          <Autocomplete
            {...register('featureClass')}
            loading={loadingFeatureClasses}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(configuration) => configuration?.name ?? ''}
            options={featureClassesResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ArcGIS Feature Class"
                error={Boolean(errors.featureClass)}
                helperText={errors.featureClass?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="civil3dFeatureType"
        render={({ field }) => (
          <Autocomplete
            {...register('civil3dFeatureType')}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(featureType) => featureType}
            options={civil3dFeatureTypesByCategory[Civil3dFeatureType.FEATURE_LINE]}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Civil 3D Feature Type"
                error={Boolean(errors.civil3dFeatureType)}
                helperText={errors.civil3dFeatureType?.message}
              />
            )}
          />
        )}
      />
    </div>
  );

  return (
    <Dialog open={true}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
        <DialogTitle>Add ArcGIS Civil 3D Feature Line</DialogTitle>
        <DialogContent>
          {renderContent()}
          {createError ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {createError?.response?.data?.message || createError?.response?.data?.message}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
            Cancel
          </Button>
          <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
            Create
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
